<template>
  <section class="hero is-default is-bold">
    <div class="hero-body">
      <div class="container">
        <div class="columns">
          <div class="column is-3">
            <b-menu>
              <b-menu-list label="Administration" v-if="authStore.isSuperUser">
                <b-menu-item
                  :active="coreStore.sections.includes(activeModule)"
                  :expanded="coreStore.sections.includes(activeModule)"
                  @click="activate(coreStore.sections[0])"
                >
                  <template slot="label" slot-scope="props">
                    <b-icon icon="cog"></b-icon>
                    {{ $t('viewAdministration.settings') }}
                    <b-icon
                      class="is-pulled-right"
                      :icon="props.expanded ? 'caret-down' : 'caret-up'"
                    >
                    </b-icon>
                  </template>

                  <b-menu-item
                    v-for="(section, index) in coreStore.sections"
                    :key="index"
                    :active="activeModule === section"
                    @click="activate(section)"
                  >
                    <template slot="label">
                      <nav class="level">
                        <div class="level-left">
                          <div class="level-item">
                            <b-icon icon="cog"></b-icon>
                            {{ $t(section) }}
                          </div>
                        </div>
                      </nav>
                    </template>
                  </b-menu-item>
                </b-menu-item>

                <b-menu-item
                  v-if="activeApps.length > 0"
                  :active="adminAppIsActive"
                  :expanded="adminAppIsActive"
                  @click="activate(firstAppName)"
                >
                  <template slot="label" slot-scope="props">
                    <b-icon icon="table"></b-icon>
                    {{ $t('viewAdministration.data') }}
                    <b-icon
                      class="is-pulled-right"
                      :icon="props.expanded ? 'caret-down' : 'caret-up'"
                    >
                    </b-icon>
                  </template>
                  <b-menu-item
                    v-for="(activeApp, index) in activeApps"
                    :key="index"
                    :active="activeModule === activeApp.name + '-admin'"
                    @click="activate(activeApp.name + '-admin')"
                  >
                    <template slot="label">
                      <nav class="level">
                        <div class="level-left">
                          <div class="level-item">
                            <b-icon :icon="activeApp.icon"></b-icon>
                            {{ $t(activeApp.name) }}
                          </div>
                        </div>
                      </nav>
                    </template>
                  </b-menu-item>
                </b-menu-item>

                <b-menu-item
                  :active="activeModule === 'users-admin'"
                  @click="activate('users-admin')"
                  data-testid="users-admin"
                >
                  <template slot="label">
                    <b-icon icon="users"></b-icon>
                    {{ $t('viewAdministration.users-admin') }}
                  </template>
                </b-menu-item>

                <b-menu-item
                  :active="activeModule === 'jobs-admin'"
                  @click="activate('jobs-admin')"
                >
                  <template slot="label">
                    <b-icon icon="cog"></b-icon>
                    {{ $t('viewAdministration.jobs-admin') }}
                  </template>
                </b-menu-item>
              </b-menu-list>
              <b-menu-list :label="$t(entityClass)">
                <b-menu-item
                  :active="activeModule === 'my-entity'"
                  @click="activate('my-entity')"
                >
                  <template slot="label">
                    <b-icon :icon="ICON"></b-icon>
                    {{ $t(entityClass) }}
                  </template>
                </b-menu-item>
                <b-menu-item
                  :active="activeModule === 'my-tags'"
                  @click="activate('my-tags')"
                >
                  <template slot="label">
                    <b-icon icon="tags"></b-icon>
                    {{ $t('viewAdministration.my-tags') }}
                  </template>
                </b-menu-item>

                <div v-for="(activeApp, index) in activeApps" :key="index">
                  <component
                    :is="activeApp.name + '-menu'"
                    v-model="activeModule"
                  />
                </div>
              </b-menu-list>
            </b-menu>
          </div>
          <div class="column">
            <settings-editor
              v-if="coreStore.sections.includes(activeModule)"
              :section="activeModule"
            />
            <div v-for="(activeApp, index) in activeApps" :key="index">
              <component
                :is="activeApp.name + '-admin'"
                v-if="activeModule === activeApp.name + '-admin'"
              />
              <component
                :is="activeApp.name + '-wrapper'"
                :activeModule="activeModule"
              />
            </div>
            <my-entity :loaded="loaded" v-if="activeModule === 'my-entity'" />
            <my-tags v-if="activeModule === 'my-tags'" />
            <users-admin v-if="activeModule === 'users-admin'" />
            <jobs-admin v-if="activeModule === 'jobs-admin'" />
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import Vue from 'vue'
import { entityFactory } from '@/models/core'
import { Background } from '@/mixins/background.js'
import { useCoreStore } from '@/stores/core.js'
import { useAdminStore } from '@/stores/admin.js'
import { useAuthenticationStore } from '@/stores/authentication.js'
import { useTransitionStore } from '@/stores/transition.js'

export default {
  name: 'Administration',
  mixins: [Background],
  components: {
    'settings-editor': () =>
      import('@/components/superadmin/SettingsEditor.vue'),
    'my-entity': () => import('@/components/admin/entity/AbstractEntity.vue'),
    'my-tags': () => import('@/components/admin/MyTags.vue'),
    'users-admin': () => import('@/components/superadmin/UsersAdmin.vue'),
    'jobs-admin': () => import('@/components/superadmin/JobsAdmin.vue')
  },
  data () {
    return {
      activeModule: 'all-ghgi',
      loaded: false
    }
  },
  setup () {
    const coreStore = useCoreStore()
    const adminStore = useAdminStore()
    const authStore = useAuthenticationStore()
    const transitionStore = useTransitionStore()
    return { coreStore, adminStore, authStore, transitionStore }
  },
  computed: {
    entity: function () {
      return this.adminStore.entity
    },
    entityClass: function () {
      return this.coreStore.entityClass
    },
    activeApps: function () {
      return this.coreStore.activeApps
    },
    firstAppName: function () {
      let name = ''
      if (this.activeApps.length > 0) {
        name = this.activeApps[0].name
      }
      return name + '-admin'
    },
    adminAppIsActive: function () {
      let isActive = false
      for (let app of this.activeApps) {
        isActive = isActive || this.activeModule === app.name + '-admin'
      }
      return isActive
    },
    ICON: function () {
      const EntityClass = entityFactory(this.entityClass)
      return EntityClass.ICON
    },
    routeParam: function () {
      return this.$route.params.name
    }
    /* canAddActions: function () {
      // FIXME : temporaire
      return true
      // return this.transitionStore.canAddActions
    } */
  },
  watch: {
    routeParam: function () {
      this.activate(this.routeParam)
    }
  },
  methods: {
    activate: function (module) {
      this.activeModule = module
      this.$router.push('/administration/' + this.activeModule).catch(() => {})
    }
  },
  mounted () {
    if (this.authStore.isAuthenticated) {
      this.loaded = false
      this.bgUI(async () => {
        this.transitionStore.init()
        await this.adminStore.getEntity()
        await this.adminStore.getAllGHGIWithoutConsumptions()
        this.loaded = true
      })
    }
    if (this.routeParam) {
      // if login from scenario and there is no ghgi available, redirect to ges admin
      if (this.entity === null) {
        this.activate('my-entity')
      } else if (
        this.routeParam === 'all-scenarios' &&
        !(this.adminStore.allGHGI.length > 0)
      ) {
        this.activate('all-ghgi')
      } else {
        this.activate(this.routeParam)
      }
    }
  },
  beforeMount () {
    // load all available apps component so menu apears if selected by user
    for (let app of this.coreStore.availableApps) {
      Vue.component(app.name + '-menu', () =>
        import('@/components/' + app.name + '/admin/UserMenu.vue')
      )
      Vue.component(app.name + '-admin', () =>
        import('@/components/' + app.name + '/admin/DataAdmin.vue')
      )
      Vue.component(app.name + '-wrapper', () =>
        import('@/components/' + app.name + '/admin/Wrapper.vue')
      )
    }
  }
}
</script>
<style>
.menu-list a {
  padding: 0.5em !important;
}
</style>
